import { z } from 'zod/v4';
import { CompanyDocumentSchema } from './company-document.js';

/**
 * One document to create from an uploaded file.
 *
 * Deliberately has **no `id`**, unlike the legacy `/v3/company-documents/bulk-create`
 * item. The insert is an upsert on `id`, so a client-chosen id let a caller
 * overwrite — and re-home — an existing document by guessing or reusing its id. No
 * frontend ever sent one; ids are generated server-side.
 */
export const BulkCreateCompanyDocumentItemSchema = z.strictObject({
  fileId: z.string().min(1),
  isPublic: z.boolean(),
  documentTypeId: z.string().min(1).nullable().optional(),
  name: z.string().min(1).nullable().optional(),
});

export type BulkCreateCompanyDocumentItem = z.infer<
  typeof BulkCreateCompanyDocumentItemSchema
>;

/**
 * `companyId` is the company the documents are filed **on** and must be in the
 * caller's network (the caller's own company always is); it defaults to the
 * caller's company. `shareWithCompanyId` auto-approves a share with a second
 * company in the same network.
 */
export const BulkCreateCompanyDocumentsBodySchema = z.strictObject({
  companyId: z.string().min(1).nullable().optional(),
  shareWithCompanyId: z.string().optional(),
  documents: z.array(BulkCreateCompanyDocumentItemSchema).min(1),
});

export type BulkCreateCompanyDocumentsBody = z.infer<
  typeof BulkCreateCompanyDocumentsBodySchema
>;

export const BulkCreateCompanyDocumentsResponseSchema = z.array(
  CompanyDocumentSchema,
);

export type BulkCreateCompanyDocumentsResponse = z.infer<
  typeof BulkCreateCompanyDocumentsResponseSchema
>;
